// Modules
import { Modal, Loading } from '@dasa-health/components-react'
import React, { useEffect, useMemo, useState } from 'react'
import { use100vh } from 'react-div-100vh'
import { useHistory, useLocation } from 'react-router-dom'

// Application
import LiviaDesktop from 'assets/Nav_livia_Desktop.jpg'
import LiviaMobile from 'assets/Nav_livia_Mobile.jpg'
import { HelpModalInstances } from 'constants/helpmodal'
import { useApp, useSignUp } from 'context'
import { useAccountMDMHook, useAppHook, useRedirectExternalUniqueToken } from 'hooks'
import { getQueryParamsValue } from 'utils'
import { useSignUpAnaytics } from 'hooks/useSignUpAnaytics'

// Components
import { TemplateContainer } from 'components'
import { SignUpHeader as Header } from 'redesign/components/organisms'
import { useSignUpStepper } from 'redesign/hooks/useSignUpStepper'
import { Wrapper } from './components'
import { LoadingContainer, StyledImage, StyledModal } from './style'
import { useRemoteConfig } from 'modules/RemoteConfig/hooks/useRemoteConfig'
import { WhyCantEditModalButton } from './components/WhyCantEditModalButton'

export const SNPersonalData: React.FC = () => {
    const history = useHistory()
    const { openToast } = useAppHook()
    const location = useLocation()
    const height = use100vh()
    const { setPersonalData: setPersonalDataMDM, checkAccountService } = useAccountMDMHook()
    const { openHelpModal } = useApp()
    const { trackEvent, dispatchAnalyticsEvent } = useSignUpAnaytics({ hasMDM: false })
    const { isFeatureEnabled } = useRemoteConfig()
    const [loading, setLoading] = useState(false)
    const [cpf, setCpf] = useState('')
    const [name, setName] = useState('')
    const [brithdate, setBrithdate] = useState('')

    const {
        personalData,
        setSeenFirstStep,
        setPersonalData,
        verifyLoginRegistration,
        hasMounted,
        setIsLoading,
        verifyPreUserRegistration,
        checkPatientFederalRevenueByCpf,
    } = useSignUp()

    const { setStep } = useSignUpStepper()

    const { verifyQueryParamsAndSaveSessionStorage } = useRedirectExternalUniqueToken()
    const [showWelcomeImage, setShowWelcomeImage] = useState(false)
    const origin = getQueryParamsValue(location, 'origem')
    const FF_AUTH_ENABLE_FEDERAL_REVENUE_CHECK = isFeatureEnabled('AUTH_ENABLE_LOGIN_OTP')

    const BRING_REVENUE_DATA_IS_ACTIVE = useMemo(() => FF_AUTH_ENABLE_FEDERAL_REVENUE_CHECK && !!cpf, [
        FF_AUTH_ENABLE_FEDERAL_REVENUE_CHECK,
        cpf,
    ])

    const BROUGHT_REVENUE_DATA = useMemo(
        () => (BRING_REVENUE_DATA_IS_ACTIVE && !!name) || (BRING_REVENUE_DATA_IS_ACTIVE && !!brithdate),
        [BRING_REVENUE_DATA_IS_ACTIVE, brithdate, name],
    )

    useEffect(() => {
        sessionStorage.setItem('@LiviaAuth:BringRevenueData', JSON.stringify(BROUGHT_REVENUE_DATA))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [BROUGHT_REVENUE_DATA])

    useEffect(() => {
        verifyQueryParamsAndSaveSessionStorage()

        if (origin === 'livia') {
            setShowWelcomeImage(true)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [origin])

    useEffect(() => {
        setStep(0, 4)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        setSeenFirstStep(true)
    }, [setSeenFirstStep])

    useEffect(() => {
        dispatchAnalyticsEvent('personalDataOnOpen')
    }, [dispatchAnalyticsEvent])

    const handleOpenHelpModal = (helpModalInstance: HelpModalInstances): void => {
        openHelpModal(helpModalInstance)
    }

    useEffect(() => {
        const signUpPersonalDataSS = JSON.parse(sessionStorage.getItem('@LiviaAuth:SignUpPersonalData')!)
        setCpf(signUpPersonalDataSS?.cpf)
    }, [])

    async function getUserData() {
        try {
            setLoading(true)
            const data = await checkPatientFederalRevenueByCpf(cpf)

            setName(data?.checkPatientFederalRevenue?.name!)
            setBrithdate(data?.checkPatientFederalRevenue?.birthDate!)

            await setPersonalData({
                ...personalData,
                name: data?.checkPatientFederalRevenue.name,
                birthDate: data?.checkPatientFederalRevenue.birthDate,
                ...((data?.checkPatientFederalRevenue.name || data?.checkPatientFederalRevenue.birthDate) && {
                    anonymousPatient: true,
                }),
            })
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        BRING_REVENUE_DATA_IS_ACTIVE && getUserData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [BRING_REVENUE_DATA_IS_ACTIVE])

    if (!hasMounted) {
        return null
    }

    const title = BROUGHT_REVENUE_DATA ? 'Confira os dados cadastrais' : 'Faça seu cadastro'
    const subtitle = BROUGHT_REVENUE_DATA
        ? 'As informações abaixo foram fornecidas pela Receita Federal com base no CPF digitado.'
        : 'Preencha as informações abaixo para criar sua conta no Nav Dasa:'

    return loading ? (
        <LoadingContainer>
            <Loading data-testid="loading-test" size={35} strokeWidth={4} />
        </LoadingContainer>
    ) : (
        <TemplateContainer>
            <Header showStepper subtitle={subtitle} title={title} />
            {BROUGHT_REVENUE_DATA && <WhyCantEditModalButton />}
            <Wrapper
                anonymousPatient={BROUGHT_REVENUE_DATA}
                checkAccountService={checkAccountService}
                handleOpenModal={handleOpenHelpModal}
                history={history}
                personalData={personalData}
                setIsLoading={setIsLoading}
                setPersonalData={setPersonalData}
                setPersonalDataMDM={setPersonalDataMDM}
                toaster={openToast}
                trackEvent={trackEvent}
                verifyLoginRegistration={verifyLoginRegistration}
                verifyPreUserRegistration={verifyPreUserRegistration}
            />
            <StyledModal heightModal={height} open={showWelcomeImage}>
                <Modal fullMobile={true} onClose={setShowWelcomeImage} open={showWelcomeImage}>
                    <StyledImage
                        data-testid="livia-img"
                        imgDesktop={LiviaDesktop}
                        imgMobile={LiviaMobile}
                        onClick={() => setShowWelcomeImage(false)}
                    />
                </Modal>
            </StyledModal>
        </TemplateContainer>
    )
}
